// src/components/common/ProductSKUSearch.jsx
import { useEffect, useRef, useState } from "react";
import inventoryAPI from "../../../../api/inventory/inventoryApi";
import { useDebounce } from "../../../../hooks/useDebounce";
import { showError } from "../../../../utils/toastUtils";

/**
 * Reusable SKU/name/barcode search dropdown.
 * Used in Quick Add, Bulk Update, PI tab — single source of truth for search UX.
 */
export default function ProductSKUSearch({
  onSelect,
  excludeIds = [],
  locationId,
  placeholder = "Search by SKU, barcode, or product name...",
  autoFocus = false,
  showStock = true,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounced = useDebounce(query, 1000);

  const excludeSet = new Set(excludeIds.map(String));

  const searchProducts = async () => {
    let cancelled = false;
    if (debounced.length < 1) {
      setResults([]);
      return;
    }

    setLoading(true);

    try {
      const query = {
        q: debounced,
        locationId,
        limit: 15,
      };
      const res = await inventoryAPI.searchProducts(query);

      if (res.code === 200) {
        const filtered = (res.data || []).filter(
          (r) => !excludeSet.has(String(r.inventoryItemId)),
        );
        setResults(filtered);
        setHighlight(0);
        setOpen(true);
      }
    } catch (err) {
      cancelled = true;
      setResults([]);
      showError(err);
    } finally {
      setLoading(false);
      cancelled = true;
    }
  };

  useEffect(() => {
    searchProducts();
  }, [debounced, locationId]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const select = (item) => {
    onSelect(item);
    setQuery("");
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      select(results[highlight]);
    } else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="position-relative" ref={containerRef}>
      <div className="input-group">
        <span className="input-group-text">
          <i className="ti ti-search" />
        </span>
        <input
          ref={inputRef}
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={handleKeyDown}
          autoFocus={autoFocus}
        />
        {loading && (
          <span className="input-group-text">
            <span className="spinner-border spinner-border-sm" />
          </span>
        )}
      </div>

      {open && results.length > 0 && (
        <ul
          className="list-group position-absolute w-100 shadow"
          style={{
            zIndex: 1050,
            maxHeight: 320,
            overflowY: "auto",
            top: "100%",
          }}
        >
          {results.map((r, idx) => (
            <li
              key={r.inventoryItemId}
              className={`list-group-item list-group-item-action ${idx === highlight ? "active" : ""}`}
              style={{ cursor: "pointer" }}
              onMouseEnter={() => setHighlight(idx)}
              onClick={() => select(r)}
            >
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <div className="fw-semibold">
                    {r.productName}
                    {r.variantName && ` — ${r.variantName}`}
                  </div>
                  <div className="small">
                    SKU: <code>{r.sku}</code>
                  </div>
                </div>
                {showStock && (
                  <div className="text-end ms-2">
                    <span
                      className={`badge ${r.stock > 0 ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`}
                    >
                      {r.stock ? `${r.stock} avail` : "∞"}
                    </span>
                    {r.mrp && (
                      <div className="small mt-1">
                        ₹{Number(r.mrp).toFixed(2)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {open && !loading && query.length >= 1 && results.length === 0 && (
        <div
          className="position-absolute w-100 shadow bg-white border rounded p-3 text-center text-muted small"
          style={{ zIndex: 1050, top: "100%" }}
        >
          No products found for "{query}"
        </div>
      )}
    </div>
  );
}
